import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Heart, Code2, Play, Pause, Monitor, Smartphone, Laptop } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const LaptopFrame = ({ children }) => (
  <div className="relative flex flex-col items-center animate-in fade-in zoom-in duration-500">
    {/* Screen */}
    <div className="relative w-[500px] h-[320px] bg-[#1E293B] rounded-t-2xl border-[12px] border-[#334155] shadow-2xl overflow-hidden flex items-center justify-center">
      <div className="absolute top-0 left-0 right-0 h-6 bg-[#0F172A] flex items-center px-4 gap-1.5 border-b border-white/5">
        <div className="w-2 h-2 rounded-full bg-[#FF5F56]" />
        <div className="w-2 h-2 rounded-full bg-[#FFBD2E]" />
        <div className="w-2 h-2 rounded-full bg-[#27C93F]" />
      </div>
      <div className="mt-6 flex-1 w-full h-full flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
        {children}
      </div>
    </div>
    {/* Base */}
    <div className="w-[580px] h-4 bg-[#475569] rounded-b-xl relative shadow-xl">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-2 bg-[#334155] rounded-b-lg" />
    </div>
  </div>
);

const PhoneFrame = ({ children }) => (
  <div className="relative w-[280px] h-[580px] bg-[#0F172A] rounded-[3rem] border-[12px] border-[#334155] shadow-2xl flex flex-col items-center overflow-hidden animate-in fade-in zoom-in duration-500">
    {/* Dynamic Island */}
    <div className="absolute top-4 w-20 h-6 bg-black rounded-full z-20 flex items-center justify-center">
      <div className="w-1.5 h-1.5 rounded-full bg-blue-500/20 mr-4" />
      <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
    </div>
    {/* Content Area */}
    <div className="flex-1 w-full h-full flex items-center justify-center bg-slate-900/30">
      {children}
    </div>
    {/* Home indicator */}
    <div className="absolute bottom-2 w-32 h-1 bg-white/20 rounded-full" />
  </div>
);

const AnimationPreview = ({
  animationName,
  settings,
  previewObject,
  onToggleFavorite,
  isFavorite,
  onOpenCodePanel
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [key, setKey] = useState(0);
  const [deviceMode, setDeviceMode] = useState('laptop'); // 'laptop' or 'phone'

  const handleReplay = useCallback(() => {
    setIsPlaying(false);
    setKey(prev => prev + 1);
    setTimeout(() => setIsPlaying(true), 50);
  }, []);

  useEffect(() => {
    handleReplay();
  }, [animationName, settings, handleReplay]);

  const getAnimationStyle = () => {
    if (!isPlaying) return { animation: 'none' };

    return {
      animation: `${animationName} ${settings.duration}ms ${settings.timingFunction} ${settings.delay}ms ${settings.iterationCount === 'infinite' ? 'infinite' : settings.iterationCount} ${settings.direction} ${settings.fillMode}`,
    };
  };

  const renderPreviewObject = (scale = 1) => {
    const commonStyle = {
      ...getAnimationStyle(),
      transform: (getAnimationStyle().transform || '') + ` scale(${scale})`
    };

    switch (previewObject.type) {
      case 'box':
        return (
          <div
            key={key}
            className={cn(
              "w-28 h-28 rounded-3xl shadow-2xl flex items-center justify-center text-white/40 font-black text-3xl transition-all duration-500 transform rotate-[10deg]",
              previewObject.variant === 'dark' ? "bg-[#1E293B] border border-white/10" : "bg-gradient-to-br from-violet-500 to-indigo-600 shadow-violet-500/20"
            )}
            style={commonStyle}
          >
            A
          </div>
        );
      case 'circle':
        return (
          <div
            key={key}
            className="w-28 h-28 rounded-[2rem] bg-gradient-to-br from-violet-500 to-indigo-600 shadow-2xl shadow-violet-500/30 transform -rotate-[5deg]"
            style={commonStyle}
          />
        );
      case 'button':
        return (
          <button
            key={key}
            className="px-10 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-black rounded-2xl text-[11px] uppercase tracking-[0.2em] shadow-2xl shadow-violet-500/20 transform rotate-1"
            style={commonStyle}
          >
            Execute
          </button>
        );
      case 'text':
        return (
          <span
            key={key}
            className="text-4xl font-black bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent tracking-tighter"
            style={commonStyle}
          >
            I ❤️ Animation
          </span>
        );
      default:
        return (
          <div
            key={key}
            className="w-28 h-28 rounded-3xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-2xl"
            style={commonStyle}
          />
        );
    }
  };

  const PlatformButton = ({ label, tab, device }) => (
    <button
      onClick={() => {
        setDeviceMode(device);
        onOpenCodePanel(tab);
      }}
      className={cn(
        "px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg",
        device === deviceMode
          ? "bg-violet-600 text-white border-violet-500"
          : "bg-white/[0.03] border-white/5 text-slate-400 hover:bg-white/[0.08]"
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex-1 flex flex-col bg-[#0F172A] relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-violet-600/[0.02] rounded-full blur-[120px] pointer-events-none" />



      {/* Preview Header / Breadcrumbs & Vertical Export Sidebar */}
      <div className="absolute top-0 left-0 bottom-0 p-10 flex flex-col gap-12 z-10 pointer-events-none w-fit">
        {/* Breadcrumbs */}
        <div className="flex flex-col gap-2 items-start">
          <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-600 font-bold">Motion Stage</span>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black text-violet-400 bg-violet-500/10 px-3 py-1 rounded-lg border border-violet-500/20 backdrop-blur-md uppercase tracking-widest text-shadow-glow">
              {animationName.split('-')[0]}
            </span>
            <span className="text-slate-700 font-light">/</span>
            <span className="text-[16px] font-black text-white tracking-widest uppercase">
              {animationName}
            </span>
          </div>
        </div>

        {/* Vertical Platform Export Buttons */}
        <div className="flex flex-col gap-5 pointer-events-auto">
          <div className="flex items-center gap-3">
            <div className="w-1 h-1 rounded-full bg-violet-500 animate-pulse" />
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em]">Quick Export</span>
          </div>
          <div className="flex flex-col gap-3 items-start">
            <PlatformButton label="CSS / HTML" tab="html-css" device="laptop" />
            <PlatformButton label="SwiftUI" tab="swiftui" device="phone" />
            <PlatformButton label="Android" tab="android" device="phone" />
            <PlatformButton label="Flutter" tab="flutter" device="phone" />
            <PlatformButton label="React Native" tab="react-native" device="phone" />
          </div>
        </div>
      </div>

      {/* Top Right Controls (Absolute) */}
      <div className="absolute top-10 right-10 flex items-center gap-3 z-10 pointer-events-auto">
        {/* Device Toggle */}
        <div className="bg-white/5 border border-white/5 p-1 rounded-2xl flex items-center mr-4">
          <button
            onClick={() => setDeviceMode('laptop')}
            className={cn(
              "p-2.5 rounded-xl transition-all",
              deviceMode === 'laptop' ? "bg-violet-600 text-white" : "text-slate-500 hover:text-slate-300"
            )}
          >
            <Laptop className="w-5 h-5" />
          </button>
          <button
            onClick={() => setDeviceMode('phone')}
            className={cn(
              "p-2.5 rounded-xl transition-all",
              deviceMode === 'phone' ? "bg-violet-600 text-white" : "text-slate-500 hover:text-slate-300"
            )}
          >
            <Smartphone className="w-5 h-5" />
          </button>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleReplay}
          className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/5 shadow-2xl text-slate-400 hover:text-violet-400 transition-all hover:scale-110 active:scale-95 hover:bg-white/[0.08]"
        >
          <RotateCcw className="w-5 h-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsPlaying(!isPlaying)}
          className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/5 shadow-2xl text-slate-400 hover:text-violet-400 transition-all hover:scale-110 hover:bg-white/[0.08]"
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleFavorite}
          className={cn(
            "w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/5 shadow-2xl transition-all hover:scale-110 hover:bg-white/[0.08]",
            isFavorite ? "text-violet-400 shadow-violet-500/20" : "text-slate-400 hover:text-violet-400"
          )}
        >
          <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
        </Button>
      </div>

      {/* Preview Area with Mockups */}
      <div className="flex-1 flex items-center justify-center relative p-20">
        <div className="relative w-full h-full flex items-center justify-center">
          {deviceMode === 'laptop' ? (
            <LaptopFrame>
              {renderPreviewObject(1)}
            </LaptopFrame>
          ) : (
            <PhoneFrame>
              {renderPreviewObject(0.8)}
            </PhoneFrame>
          )}
        </div>
      </div>



      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes loadingBar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        .text-shadow-glow {
          text-shadow: 0 0 8px rgba(139, 92, 246, 0.4);
        }
      `}} />
    </div>
  );
};

export default AnimationPreview;
